/**
 * SL (section leader) post-section survey document type and Firebase path.
 *
 * Defines the path and document shape for section leader survey responses.
 * Shared between webapp and functions. Ref/converters live in firebase/client and firebase/admin.
 */

import {z} from 'zod';

import {
  parseSectionId,
  parseSectionWeekId,
  type SectionId,
  type SectionWeekId,
} from '../../../section/sectionApi';
import type {CourseId, UserId} from '../../../types/branded';
import {FirestoreTimestampSchema} from '../../types';
import {
  SurveyResponseValueSchema,
  type SurveyResponseValue,
} from './postSectionSurvey';
import {
  getSectionAnalysisWeekChildCollectionPath,
  SECTION_ANALYSIS_SL_DEBRIEFS_SUBCOLLECTION,
} from './sectionAnalysisFirestorePaths';

// ============================================================================
// Schema (Zod)
// ============================================================================

/**
 * Schema for SL post-section survey Firestore document.
 * Stored at:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slDebriefs/{userId}`
 */
export const SLPostSectionSurveyDocumentSchema = z
  .object({
    latestSubmission: FirestoreTimestampSchema.optional(),
    participation: z
      .record(z.string(), z.record(z.string(), z.boolean()))
      .optional(),
    responses: z.record(z.string(), SurveyResponseValueSchema).optional(),
    sectionId: z
      .string()
      .optional()
      .refine(s => s === undefined || parseSectionId(s) != null, {
        message: 'Invalid sectionId',
      })
      .transform((s): SectionId | undefined => parseSectionId(s)),
    sectionWeek: z
      .number()
      .optional()
      .refine(n => n === undefined || parseSectionWeekId(n) != null, {
        message: 'Invalid sectionWeekId',
      })
      .transform((n): SectionWeekId | undefined => parseSectionWeekId(n)),
    userId: z
      .string()
      .optional()
      .transform((s): UserId | undefined => s),
    /**
     * Denormalized course identifier. Optional for back-compat with debriefs
     * written before this field was tracked; new writes always include it.
     * Enables `collectionGroup` queries filtered by course (used by the admin
     * dashboard's SL self-rating histogram aggregation). Firestore rules
     * enforce that the stamped value matches the `{courseId}` path segment so
     * an SL cannot spoof their debrief into a different course's stats.
     */
    courseId: z.string().optional(),
  })
  .loose();

/** Section leader post-section survey Firestore document. */
export type SLPostSectionSurveyDocument = z.infer<
  typeof SLPostSectionSurveyDocumentSchema
>;

// ============================================================================
// Paths
// ============================================================================

/**
 * Constructs the path to the SL debrief / post-section survey collection.
 * Collection path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slDebriefs`
 */
export function getSLPostSectionSurveyCollectionPath({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
}): string {
  return getSectionAnalysisWeekChildCollectionPath({
    courseId,
    sectionId,
    sectionWeek,
    leafSubcollection: SECTION_ANALYSIS_SL_DEBRIEFS_SUBCOLLECTION,
  });
}

/**
 * Constructs the Firestore document path for an SL post-section survey.
 * Path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slDebriefs/{userId}`
 */
export function getSLPostSectionSurveyDocumentPath({
  courseId,
  sectionId,
  sectionWeek,
  userId,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  userId: UserId;
}): string {
  return `${getSLPostSectionSurveyCollectionPath({courseId, sectionId, sectionWeek})}/${userId}`;
}

// ============================================================================
// Response type and builder
// ============================================================================

/** Question ID to response value for SL survey (same shape as student survey responses). */
export type SLPostSectionSurveyResponse = Record<string, SurveyResponseValue>;

/** Participation state: per-user map of participation keys to booleans. */
export type SLParticipationState = Record<UserId, Record<string, boolean>>;

/** Builds an SL post-section survey document from structured data. */
export function createSLPostSectionSurveyDocument(data: {
  responses: SLPostSectionSurveyResponse;
  userId: UserId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  participation: SLParticipationState;
  courseId: CourseId;
}): SLPostSectionSurveyDocument {
  return {
    responses: data.responses,
    userId: data.userId,
    sectionId: data.sectionId,
    sectionWeek: data.sectionWeek,
    participation: data.participation,
    courseId: data.courseId,
  };
}
